import { selectActiveOrganizationId, selectActiveUserAccountId } from "@somewear/auth";
import { notificationsSlice } from "@somewear/notification";
import { resolveDocsUrl } from "@web/common/docsUrl";
import Paths from "@web/common/Paths";
import Config from "@web/config/Config";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { v4 as uuid } from "uuid";

import { docsSessionErrorMessage, requestDocsSession } from "./docs.api";
import { buildDocsEntryUrl, resolveDocsRedirectUrl, submitDocsHandoff } from "./docs.utils";

function useDocsErrorNotification() {
	const dispatch = useDispatch();
	return useCallback(
		(error: unknown) => {
			dispatch(
				notificationsSlice.actions.emitErrorNotification({
					requestId: uuid(),
					message: docsSessionErrorMessage(error),
					data: undefined,
				}),
			);
		},
		[dispatch],
	);
}

export function useDocsNavigation() {
	const docsUrl = resolveDocsUrl(Config.somewear.docsUrl, window.location.origin);
	const notifyError = useDocsErrorNotification();
	const openDocs = useCallback(() => {
		if (!docsUrl) return;
		try {
			// Open without credentials; the Docs gate requests a session if needed.
			window.open(
				buildDocsEntryUrl(docsUrl, "/", window.location.origin),
				"_blank",
				"noopener,noreferrer",
			);
		} catch (error) {
			console.error("Unable to open Docs", error);
			notifyError(error);
		}
	}, [docsUrl, notifyError]);

	return { docsUrl, openDocs };
}

export function useDocsRedirect(
	isAuthServiceLoaded: boolean,
	activeAuthId: string | undefined,
): { isRedirecting: boolean } {
	const navigate = useNavigate();
	const accountId = useSelector(selectActiveUserAccountId);
	const organizationId = useSelector(selectActiveOrganizationId);
	const notifyError = useDocsErrorNotification();
	const [isRedirecting, setIsRedirecting] = useState(false);
	const hasAttemptedRedirect = useRef(false);
	// Preserve the original destination even if login changes the browser URL.
	const initialDestination = useRef<{
		returnTo: string | null;
		requestedDocsOrigin: string | null;
	} | null>(null);
	if (initialDestination.current === null) {
		const params = new URLSearchParams(window.location.search);
		initialDestination.current = {
			returnTo:
				window.location.pathname === Paths.DocsRedirect
					? (params.get("returnTo") ?? "/")
					: null,
			requestedDocsOrigin: params.get("docsOrigin"),
		};
	}
	const { returnTo, requestedDocsOrigin } = initialDestination.current;

	useEffect(() => {
		if (
			returnTo === null ||
			!isAuthServiceLoaded ||
			activeAuthId === undefined ||
			hasAttemptedRedirect.current
		)
			return;

		hasAttemptedRedirect.current = true;
		setIsRedirecting(true);
		void (async () => {
			try {
				const docsUrl = resolveDocsUrl(Config.somewear.docsUrl, window.location.origin);
				if (
					!docsUrl ||
					new URL(docsUrl, window.location.origin).origin === window.location.origin
				) {
					throw new Error("This deployment has no external Docs URL");
				}
				const session = await requestDocsSession({
					accountId,
					organizationId,
					requestedDocsOrigin:
						requestedDocsOrigin ?? new URL(docsUrl, window.location.origin).origin,
				});
				// Only the authenticated API response may choose where credentials are sent.
				const redirectUrl = resolveDocsRedirectUrl(session.docsOrigin);
				submitDocsHandoff(
					redirectUrl,
					session.accessToken,
					session.authorizationContext,
					returnTo,
					window.location.origin,
				);
			} catch (error) {
				console.error("Unable to enter Docs", error);
				notifyError(error);
				setIsRedirecting(false);
				navigate(Paths.App, { replace: true });
			}
		})();
	}, [
		accountId,
		activeAuthId,
		isAuthServiceLoaded,
		navigate,
		notifyError,
		organizationId,
		requestedDocsOrigin,
		returnTo,
	]);

	return { isRedirecting };
}
